import {
  Stack,
  Input,
  InputAdornment,
  Tooltip,
  IconButton,
} from "@mui/material";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import React from "react";
import CFormErrors from "./CFormErrors";
import TextFieldInput from "components/Atoms/Input/TextField/TextFieldInput";
import { useThemePalette } from "common/hooks/theme_palette";

const PositiveNumberInput = React.forwardRef((props, ref) => {
  const handleChange = (event) => {
    const inputValue = event.target.value;
    // Only allow positive numbers and disallow "-"
    if (inputValue === "" || !inputValue.includes("-")) {
      props.onChange(event);
    }
  };

  return <Input {...props} ref={ref} onChange={handleChange} />;
});

const CInput = ({
  label,
  placeholder,
  type = "text",
  name,
  formik,
  hint,
  EndAdornment,
  ...rest
}) => {
  const { inputBg, textGray } = useThemePalette();
  const multinames = name?.split(".")?.map((data) => data);
  const value =
    multinames?.length === 2
      ? formik?.values?.[multinames[0]]?.[multinames[1]]
        ? formik?.values?.[multinames[0]]?.[multinames[1]]
        : ""
      : formik?.values[name];
  const errorsMsg =
    multinames?.length === 2
      ? formik?.errors?.[multinames[0]]?.[multinames[1]]
        ? formik?.errors?.[multinames[0]]?.[multinames[1]]
        : ""
      : formik.errors[name];

  // Native time/datetime inputs break when wrapped in empty MUI adornments
  // (value may not sync to Formik). Pass {} so TextFieldInput skips defaults.
  const mergedInputProps =
    type === "time" || type === "datetime-local" || type === "date"
      ? {}
      : type === "number" || EndAdornment
        ? {
            ...(type === "number"
              ? { inputComponent: PositiveNumberInput }
              : {}),
            ...(EndAdornment
              ? {
                  endAdornment: (
                    <InputAdornment position="end">{EndAdornment}</InputAdornment>
                  ),
                }
              : {}),
          }
        : undefined;

  const labelWithHint = hint ? (
    <Stack
      direction="row"
      alignItems="center"
      gap={0.5}
      component="span"
      display="inline-flex"
    >
      {label}
      <Tooltip
        title={hint}
        arrow
        placement="top"
        componentsProps={{
          tooltip: {
            sx: {
              fontSize: "14px",
              fontFamily: "Cairo",
              lineHeight: 1.5,
              maxWidth: 280,
              py: 1,
              px: 1.5,
            },
          },
        }}
      >
        <IconButton
          size="small"
          sx={{ p: 0.25, color: textGray }}
          aria-label={hint}
        >
          <ErrorOutlineIcon sx={{ fontSize: 18 }} />
        </IconButton>
      </Tooltip>
    </Stack>
  ) : (
    label
  );

  return (
    <Stack mb="5" position="relative">
      <TextFieldInput
        InputProps={mergedInputProps}
        EndAdornment={!mergedInputProps ? EndAdornment : null}
        id={name}
        type={type}
        label={labelWithHint}
        aria-autocomplete="none"
        autoComplete={false}
        inputProps={{
          autoComplete: "new-password",
          form: {
            autocomplete: "off",
          },
        }}
        bgColor={inputBg}
        labelStyle={{
          color: "#565656",
          fontSize: "18px",
          fontFamily: "Cairo",
          mb: 2,
        }}
        placeholder={placeholder}
        sx={{
          "& .MuiOutlinedInput-root": {
            "& fieldset": {
              borderColor: "transparent",
            },
            "&:hover fieldset": {
              borderColor: "transparent",
            },
            "&.Mui-focused fieldset": {
              borderColor: "transparent",
            },
          },
        }}
        {...rest}
        onChange={(e) => {
          if (type === "time" || type === "datetime-local" || type === "date") {
            formik?.setFieldValue(name, e.target.value);
            return;
          }
          formik?.handleChange(e);
        }}
        value={value ?? ""}
        onBlur={formik?.handleBlur}
        name={name}
      />

      <CFormErrors errorsMsg={errorsMsg} />
    </Stack>
  );
};

export default CInput;
